import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import DocumentPackages from "@/components/documents/DocumentPackages";
import { Skeleton } from "@/components/Skeleton";
import { FileText, Search, Wand2 } from "lucide-react";
import DocumentWorkflowBuilder, {WORKFLOW_TYPES} from "@/components/documents/DocumentWorkflowBuilder";
import { TotoAdaptiveDock } from "@/components/toto/TotoAdaptiveSurface";
import { toast } from "sonner";
import "./documents-space.css";

const KIND_LABELS = {
  guide:     "Guides",
  process:   "Processes",
  policy:    "Policies",
  reference: "Reference",
  form:      "Forms",
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const daysAgo = Math.round((Date.now() - d.getTime()) / 86400000);
    if (daysAgo === 0) return "today";
    if (daysAgo === 1) return "yesterday";
    if (daysAgo < 30) return `${daysAgo}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

export default function DocumentsPage() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [docs, setDocs] = useState(null);
  const [reference, setReference] = useState({ kinds: [], categories: [], boutiques: [], can_edit: false });
  const [kindFilter, setKindFilter] = useState("all");
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const [d, r] = await Promise.all([
        api.get("/documents", { params: q ? { q } : {} }),
        api.get("/documents/reference"),
      ]);
      setDocs((d.data.documents || []).filter(doc => doc.status !== "archived"));
      setReference(r.data);
    } catch (e) {
      setLoadError(e.response?.status === 401 ? "Your session has expired. Sign in again to open Documents." : (e.response?.data?.detail || "Documents could not be loaded."));
      toast.error("Could not load documents");
    }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const visibleDocs = (docs || []).filter(d => kindFilter === "all" || (kindFilter.startsWith("workflow:") ? d.workflow?.document_type === kindFilter.slice(9) : !d.workflow && d.kind === kindFilter));
  const totalVisible = visibleDocs.length;

  const packages = reference.can_manage_packages && searchParams.get("view") === "packages";
  const studio = reference.can_edit && showNew && !packages;

  return (
    <main className="documents-space" data-testid="documents-page">
      <section className="documents-space__vessel" aria-labelledby="documents-title">
        <header className="documents-space__header">
          <div>
            <h1 id="documents-title">Documents</h1>
          </div>
          {reference.can_edit && !studio && !packages && (
            <button
              onClick={() => setShowNew(true)}
              data-testid="btn-new-document"
              className="documents-space__new"
            >
              <Wand2 size={14} strokeWidth={1.75} />
              New document
            </button>
          )}
        </header>

        {!studio && reference.can_manage_packages && <nav className="documents-package-navigation" aria-label="Documents views"><button aria-current={!packages ? "page" : undefined} onClick={() => setSearchParams({})}>Library</button><button aria-current={packages ? "page" : undefined} onClick={() => {setShowNew(false);setSearchParams({view:"packages"});}}>Packages</button></nav>}
        {!packages && !studio && docs !== null && (docs.length > 0 || q || kindFilter !== "all") && <div className="documents-space__tools">
          <div className="documents-space__search relative">
            <Search size={12} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              aria-label="Search documents"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="documents-search"
              placeholder="Search documents…"
              className="w-full pl-9 pr-4 py-2 rounded-full text-sm outline-none"
            />
          </div>
          <select aria-label="Document type" className="documents-space__type" value={kindFilter} onChange={e => setKindFilter(e.target.value)}>
            <option value="all">All types</option>
            {WORKFLOW_TYPES.map(([id,label]) => <option key={id} value={`workflow:${id}`}>{label}</option>)}
            {reference.kinds.map(k => <option key={k} value={k}>{KIND_LABELS[k]} (existing)</option>)}
          </select>
        </div>}

        <section className="documents-space__content">
        {loadError ? <div role="alert"><p>{loadError}</p>{loadError.includes("Sign in") && <Link to="/login" className="documents-space__new">Sign in</Link>}<button className="documents-space__new" onClick={load}>Try again</button></div> : packages ? <DocumentPackages/> : studio ? (
          <DocumentWorkflowBuilder reference={reference} onBack={() => setShowNew(false)} onCreated={(id) => nav(`/documents/${id}`)} onCancel={docs?.length ? () => setShowNew(false) : undefined} />
        ) : docs === null ? (
          <div className="space-y-3">
            <Skeleton className="h-24 rounded-2xl" />
            <Skeleton className="h-24 rounded-2xl" />
          </div>
        ) : totalVisible === 0 ? (
          q || kindFilter !== "all" ? <div className="documents-space__empty"><h3>No matching documents</h3><p>Try another search or show every category.</p><button className="documents-space__new" onClick={() => { setQ(""); setKindFilter("all"); }}>Clear filters</button></div> : <EmptyState canEdit={reference.can_edit} />
        ) : (
          <div className="documents-space__results">
            <div className="documents-library-heading" aria-hidden="true"><span>Document</span><span>Owner</span><span>Updated</span></div>
            {visibleDocs.map(d => <DocumentCard key={d.id} doc={d} />)}
            <p className="documents-library-count">{totalVisible} {totalVisible === 1 ? "document" : "documents"}</p>
          </div>
        )}
        </section>
      </section>

      <TotoAdaptiveDock active="workspace" />
    </main>
  );
}

function DocumentCard({ doc }) {
  return <Link to={`/documents/${doc.id}`} data-testid={`document-card-${doc.id}`} className="documents-library-row">
    <div className="documents-library-description"><FileText size={24} strokeWidth={1.5} aria-hidden="true" /><div>
      <h2>{doc.title}{doc.status !== "published" && <span className="documents-library-status">{doc.status === "archived" ? "Archived" : "Draft"}</span>}</h2>
      <p>{WORKFLOW_TYPES.find(([id]) => id === doc.workflow?.document_type)?.[1] || KIND_LABELS[doc.kind] || doc.kind}{doc.category && ` · ${doc.category}`}{doc.boutique_scope?.length > 0 && ` · ${doc.boutique_scope.join(", ")}`}</p>
      {doc.why_this_matters && <p className="documents-library-summary">{doc.why_this_matters}</p>}
    </div></div>
    <span className="documents-library-owner">{doc.owner_name || "—"}</span><span className="documents-library-updated">{fmtDate(doc.updated_at)}</span>
  </Link>;
}

function EmptyState({ canEdit = false }) {
  return (
    <div className="documents-space__empty" data-testid="documents-empty">
      <div className="documents-space__empty-icon">
        <FileText size={20} strokeWidth={1.25} />
      </div>
      <h3>{canEdit ? "No active documents" : "The library starts here."}</h3>
      <p>
        {canEdit ? "Choose New document when you’re ready to add one." : "Published guides, processes, and policies will appear here when your leadership team shares them."}
      </p>
    </div>
  );
}
